import { Loja } from '@/models'
import { Endereco } from '@/models/Endereco'
import { ItemDeRomaneio } from '@/models/Romaneio'
import pdfMake from 'pdfmake/build/pdfmake'
import type { Content, ContentText, TDocumentDefinitions } from 'pdfmake/interfaces'
import vfs from 'pdfmake/build/vfs_fonts.js'
import print from 'print-js'
import JsBarcode from 'jsbarcode'
import { dateTimeToPtBrFormat } from '../date/date-utils'
import { formatarMoeda } from '@/shareds/formatadores'

function textToBase64Barcode(text: string) {
	const canvas = document.createElement("canvas");
	JsBarcode(canvas, text, {
		height: 60,
		fontSize: 15,
		displayValue: true,
	});
	return canvas.toDataURL("image/png");
}

window.pdfMake.vfs = vfs.pdfMake.vfs;

export interface DadosRomaneioDeEstoque {
	identificador: string
	tipoMovimentacao: string
	dataHoraCriacao: Date | string | null
	observacao: string
	usuario: string
	lojaOrigem: Loja | null
	lojaDestino: Loja | null
	itens: ItemDeRomaneio[]
}

function montarEndereco(endereco?: Endereco | null): string {
	if (!endereco) return 'Não consta'
	const logradouro = [endereco.rua, endereco.numero].filter(Boolean).join(', ')
	const bairroCidade = [endereco.bairro, endereco.cidade && `${endereco.cidade} - ${endereco.uf}`]
		.filter(Boolean)
		.join(' - ')
	return [logradouro, bairroCidade, endereco.cep && `CEP: ${endereco.cep}`]
		.filter(Boolean)
		.join(' - ') || 'Não consta'
}

function buildBlocoDaLoja(titulo: string, loja: Loja | null): any {
	const linhas: [string, string][] = [
		['LOJA:', loja?.nomeFantasia || loja?.razaoSocial || 'Não consta'],
		['CNPJ:', loja?.cnpj || 'Não consta'],
		['ENDEREÇO:', montarEndereco(loja?.endereco)],
		['TELEFONE:', loja?.telefones?.length ? loja.telefones.join(', ') : 'Não consta'],
	]

	return {
		width: '*',
		stack: [
			{ text: titulo, bold: true, fontSize: 11, margin: [0, 0, 0, 4] },
			...linhas.map(([campo, valor]) => ({
				columns: [
					{ text: campo, bold: true, width: 'auto', margin: [0, 0, 5, 0] },
					{ text: valor },
				],
			})),
		],
	}
}

export async function gerarRomaneioDeEstoque(dados: DadosRomaneioDeEstoque): Promise<string> {
	const docDefinition: TDocumentDefinitions = {
		pageSize: 'A4',
		content: [
			{
				table: {
					widths: ['*'],
					body: [
						[
							{
								columns: [
									{ text: 'ROMANEIO', fontSize: 16, style: 'tableHeader', width: 'auto', bold: true },
									{ text: dados.tipoMovimentacao || '', fontSize: 11, style: 'tableHeader', width: '*', bold: true, alignment: 'center', margin: [0, 4, 0, 0] },
									{ text: `${dados.identificador}`, fontSize: 15, style: 'tableHeader', width: 'auto', bold: true },
								],
							},
						],
						[{
							columns: [
								buildBlocoDaLoja('LOJA DE ORIGEM', dados.lojaOrigem),
								buildBlocoDaLoja('LOJA DE DESTINO', dados.lojaDestino),
							],
							columnGap: 20,
						}],
						[{
							stack: [
								['USUÁRIO:', dados.usuario || 'Não consta'],
								['DATA:', dados.dataHoraCriacao ? dateTimeToPtBrFormat(dados.dataHoraCriacao.toString()) : ''],
								['OBSERVAÇÃO:', dados.observacao || 'Sem observação'],
							].map(([campo, valor]) => ({
								columns: [
									{ text: campo, bold: true, width: 'auto', margin: [0, 0, 5, 0] },
									{ text: valor },
								],
							})),
						}],
					],
				},
				margin: [0, 0, 0, 10],
			},
			{ image: textToBase64Barcode(`${dados.identificador}` || ''), alignment: 'center' },
			buildTabelaItensDoRomaneio(dados.itens || []),
			buildTabelaQuantidadeTotal(dados.itens || []),
		],
	}
	return new Promise(resolve => {
		pdfMake.createPdf(docDefinition).getBase64((result) => resolve(result))
	})
}

function buildTabelaItensDoRomaneio(itens: ItemDeRomaneio[]): Content {
	return {
		table: {
			widths: [70, 95, '*', 40, 75],
			headerRows: 1,
			body: buildLinhasDosItens(itens),
		},
	}
}

function buildTabelaQuantidadeTotal(itens: ItemDeRomaneio[]): Content {
	let quantidadeTotal = 0
	let valorTotal = 0

	itens.forEach((item) => {
		quantidadeTotal += item.quantidade
		valorTotal += (item.produto.preco || 0) * item.quantidade
	})

	return {
		table: {
			widths: ['*'],
			body: [
				[
					{
						columns: [
							{ text: 'TOTAL: ', fontSize: 12, width: '*', bold: true, alignment: 'center' },
							{ text: `${quantidadeTotal}`, fontSize: 11, bold: true, width: 40, alignment: 'center', margin: [0, 0, 20, 0] },
							{ text: `R$ ${formatarMoeda(valorTotal)}`, fontSize: 11, bold: true, width: 75, alignment: 'center' },
						],
					},
				],
			],
		},
	}
}

function buildLinhasDosItens(itens: ItemDeRomaneio[]): ContentText[][] {
	const body: ContentText[][] = []
	body.push([
		{ text: 'Referencia', bold: true, fontSize: 10, alignment: 'center' },
		{ text: 'Codigo', bold: true, fontSize: 10, alignment: 'center' },
		{ text: 'Produto', bold: true, fontSize: 10, alignment: 'center' },
		{ text: 'Qtde', bold: true, fontSize: 10, alignment: 'center' },
		{ text: 'Valor', bold: true, fontSize: 10, alignment: 'center' },
	])

	const itensAgrupados: any[] = []
	itens.forEach((item) => {
		const skuPai = item.produto.tipo === 'Variante' ? `${item.produto.skuPai}` : 'Sem Variante'
		const codigo = item.produto.eans?.find(ean => ean && ean.trim() !== '') || item.produto.sku || ''
		const valorUnitario = item.produto.preco || 0

		const indice = itensAgrupados.findIndex(
			itemAgrupado =>
				itemAgrupado.nome === (item.produto.nomeCompleto || item.produto.nome) &&
				itemAgrupado.sku === item.produto.sku &&
				itemAgrupado.skuPai === skuPai &&
				itemAgrupado.valorUnitario === valorUnitario,
		)

		if (indice === -1) {
			itensAgrupados.push({
				nome: item.produto.nomeCompleto || item.produto.nome,
				quantidade: item.quantidade,
				sku: item.produto.sku,
				codigo,
				skuPai,
				valorUnitario,
			})
		} else {
			itensAgrupados[indice].quantidade += item.quantidade
		}
	})

	itensAgrupados.forEach(item => {
		body.push([
			{ text: item.skuPai, bold: false, fontSize: 10, alignment: 'center' },
			{ text: item.codigo, bold: false, fontSize: 10, alignment: 'center' },
			{ text: item.nome, bold: false, fontSize: 10, alignment: 'center' },
			{ text: `${item.quantidade}`, bold: false, fontSize: 10, alignment: 'center' },
			{ text: `R$${formatarMoeda(item.valorUnitario * item.quantidade)}`, bold: false, fontSize: 10, alignment: 'center' },
		])
	})
	return body
}

export async function imprimirRomaneioDeEstoque(dados: DadosRomaneioDeEstoque): Promise<void> {
	const romaneioBase64: string = await gerarRomaneioDeEstoque(dados)
	return new Promise((resolve, reject) => {
		print({
			printable: romaneioBase64,
			type: 'pdf',
			base64: true,
			onError: reject,
			onLoadingEnd: resolve,
		})
	})
}
